// src/pages/Manager/index.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  The floor this morning sits under Waiting on you: who warmed
//               up, who nobody has recognised this week, marks as moments, and
//               where everybody is in the thirteen. FloorThisMorning.jsx.
//   2026-09-24  New. The front door. The reference moved to System.jsx.
//
// A MANAGER'S DAY, NOT A MENU OF TOOLS.
//
// The manager layer had grown to five places: this page, the per step view, the
// morning meeting, the one on one and the team view. Every one of them is
// useful and none of them answered the question somebody actually opens this
// with, which is what is not done yet.
//
// So the front door is his own six daily disciplines with live state against
// them, and three of the six say plainly that they happen in the CRM rather
// than here. See managerDay.js for why saying so beats hiding them.
//
// WAITING ON YOU GOES FIRST, always, because it is the only part of this screen
// where somebody else is held up. A salesperson who passed a check three days
// ago and is still waiting has been stopped by their manager, not by the
// method.
//
// IT REPORTS, IT DOES NOT GRADE. No percentage of the day complete, no streak
// on the disciplines, no red unless a person is genuinely waiting. His own
// scorecard says "Recognize process behaviors, not only deliveries", and a
// screen that only shows a manager what they have not finished is the opposite
// of that.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, HStack, VStack, Text, Button, Spinner, Alert, AlertIcon, Badge } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronRight, Sun, MessageSquare, ClipboardCheck, Clock, Sparkles } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { displayNameFor } from '../../lib/names'
import { MANAGEMENT_SYSTEM } from '../../content/manager'
import { getToday } from '../../lib/managerAudit'
import { listSignoffs } from '../../lib/signoffs'
import { listFloor, listRecent as listOneOnOnes, listDue } from '../../lib/oneOnOne'
import { getMeeting, meetingState } from '../../lib/morningMeeting'
import FloorThisMorning from './FloorThisMorning.jsx'
import { dayKey } from '../../lib/localDate'
import {
  DISCIPLINES, staleOneOnOnes, heldToday, waitingOnYou, dayHeadline, ONE_ON_ONE_STALE_DAYS,
} from '../../lib/managerDay'

const ICONS = { morning: Sun, oneOnOnes: MessageSquare, audit: ClipboardCheck }

function Row({ to, icon: Icon, name, state, detail, muted }) {
  const body = (
    <HStack
      justify="space-between"
      align="center"
      py={3.5}
      borderTop="1px solid"
      borderColor="line"
      spacing={4}
      opacity={muted ? 0.72 : 1}
    >
      <HStack spacing={3} align="flex-start" flex={1} minW={0}>
        <Box pt="2px" color={muted ? 'inkDim' : 'ink'} flexShrink={0}>
          {Icon ? <Icon size={16} aria-hidden="true" /> : <Box w="16px" h="16px" />}
        </Box>
        <Box minW={0}>
          <Text fontWeight={600} fontSize="body">
            {name}
          </Text>
          {detail && (
            <Text fontSize="sm" color="inkMuted" lineHeight={1.5} maxW="measure">
              {detail}
            </Text>
          )}
        </Box>
      </HStack>
      <HStack spacing={2} flexShrink={0}>
        {state && (
          <Text fontSize="sm" fontFamily="mono" color={state.tone || 'inkDim'} whiteSpace="nowrap">
            {state.text}
          </Text>
        )}
        {to && <ChevronRight size={15} aria-hidden="true" color="currentColor" />}
      </HStack>
    </HStack>
  )
  if (!to) return body
  return (
    <Box as={RouterLink} to={to} _hover={{ bg: 'rail' }} display="block" mx={-3} px={3} borderRadius="sm">
      {body}
    </Box>
  )
}

export default function ManagerDay() {
  const { user, profile } = useAuth()
  const dealershipId = profile?.dealership_id || null
  const today = dayKey()

  const [data, setData] = useState({
    floor: [], oneOnOnes: [], due: [], meeting: null, audit: null, signoffs: [],
  })
  const [state, setState] = useState({ loading: true, error: null })

  const load = useCallback(async () => {
    setState({ loading: true, error: null })
    const [f, o, d, m, a, s] = await Promise.all([
      listFloor(dealershipId),
      listOneOnOnes(dealershipId, 60),
      listDue(dealershipId),
      getMeeting(dealershipId, today),
      getToday(user?.id),
      listSignoffs(),
    ])
    setData({
      floor: f.data,
      oneOnOnes: o.data,
      due: d.data,
      meeting: m.data,
      audit: a.data,
      signoffs: (s.signoffs || []).filter((r) => r.status === 'requested'),
    })
    const err = f.error || o.error || d.error || m.error || a.error
    setState({ loading: false, error: err ? err.message || String(err) : null })
  }, [dealershipId, user?.id, today])

  useEffect(() => {
    load()
  }, [load])

  const nameOf = useMemo(() => {
    const map = {}
    for (const p of data.floor) map[p.id] = displayNameFor(p) || 'Somebody'
    return map
  }, [data.floor])

  const held = heldToday(data.oneOnOnes, today)
  const stale = staleOneOnOnes(data.floor, data.oneOnOnes, today)
  const waiting = waitingOnYou({ signoffs: data.signoffs, dueInspections: data.due })
  const mState = meetingState(data.meeting)
  const auditDone = data.audit?.items?.length || 0
  const auditTotal = MANAGEMENT_SYSTEM.managerDailyAudit.length

  const headline = dayHeadline({
    morning: mState,
    heldCount: held.length,
    floorSize: data.floor.length,
    waiting: waiting.length,
  })

  const stateFor = {
    morning:
      mState === 'held'
        ? { text: 'held', tone: 'success' }
        : mState === 'prepared'
          ? { text: 'prepared', tone: 'warn' }
          : mState === 'unprepared'
            ? { text: 'no agenda', tone: 'danger' }
            : { text: 'not prepared', tone: 'inkDim' },
    oneOnOnes:
      data.floor.length === 0
        ? { text: 'no floor', tone: 'inkDim' }
        : { text: `${held.length} of ${data.floor.length}`, tone: held.length ? 'success' : 'inkDim' },
    audit: { text: `${auditDone} of ${auditTotal}`, tone: auditDone === auditTotal ? 'success' : 'inkDim' },
  }

  if (state.loading) {
    return (
      <VStack align="stretch" maxW="sheet" spacing={6}>
        <Spinner size="sm" />
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={10} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </Text>
        <Text textStyle="pageTitle">Running the floor</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          {headline}
        </Text>
      </Box>

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!dealershipId && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          Your account is not attached to a store, so there is no floor here yet.
        </Alert>
      )}

      {/* FIRST, ALWAYS. The only part of this screen where somebody else is
          held up by the person reading it. */}
      {waiting.length > 0 && (
        <Box>
          <HStack align="baseline" justify="space-between" mb={2} flexWrap="wrap" gap={2}>
            <Text fontSize="lg" fontWeight={600}>
              Waiting on you
            </Text>
            <Badge colorScheme="red" fontFamily="mono" fontSize="xs">
              {waiting.length}
            </Badge>
          </HStack>
          <VStack align="stretch" spacing={0}>
            {waiting.map((w) => (
              <HStack
                key={`${w.kind}-${w.id}`}
                py={3}
                borderTop="1px solid"
                borderColor="line"
                justify="space-between"
                align="baseline"
                flexWrap="wrap"
                gap={2}
              >
                <HStack spacing={3} align="baseline">
                  <Clock size={14} aria-hidden="true" />
                  <Text fontWeight={600} fontSize="body">
                    {nameOf[w.userId] || 'Somebody on your floor'}
                  </Text>
                  <Text fontSize="sm" color="inkMuted">
                    {w.kind === 'signoff'
                      ? `is waiting on a sign off for Step ${w.stepNumber ?? ''}`.trim()
                      : 'is due another look after your last one on one'}
                  </Text>
                </HStack>
                <Button
                  as={RouterLink}
                  to={w.kind === 'signoff' ? '/team/' : '/manager/one-on-one/'}
                  size="xs"
                  variant="outline"
                >
                  {w.kind === 'signoff' ? 'Sign off' : 'Go and look'}
                </Button>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      {/* SECOND, THE PEOPLE. Before the checklist, because the thing a manager
          walks in wanting is to look at the floor, and the recognition line
          in here is the weekly item on his rhythm that nothing else prompts. */}
      <FloorThisMorning floor={data.floor} dealershipId={dealershipId} userId={user?.id} />

      {/* HIS SIX. Three of them live in the CRM and say so. */}
      <Box>
        <Text fontSize="lg" fontWeight={600} mb={0.5}>
          Your day
        </Text>
        <Text fontSize="body" color="inkMuted" mb={2} maxW="measure">
          The six disciplines, as you wrote them.
        </Text>
        <VStack align="stretch" spacing={0}>
          {DISCIPLINES.map((d) => (
            <Row
              key={d.key}
              to={d.here ? d.to : null}
              icon={ICONS[d.key]}
              name={d.name}
              muted={!d.here}
              state={d.here ? stateFor[d.key] : { text: 'your CRM', tone: 'inkDim' }}
              detail={d.here ? null : d.why}
            />
          ))}
        </VStack>
      </Box>

      {stale.length > 0 && (
        <Box>
          <Text fontSize="lg" fontWeight={600} mb={0.5}>
            Nobody has sat down with these people
          </Text>
          <Text fontSize="body" color="inkMuted" mb={2} maxW="measure">
            Not in the last {ONE_ON_ONE_STALE_DAYS} days. Your rule is daily, and this is the list
            that quietly gets away from everybody.
          </Text>
          <VStack align="stretch" spacing={0}>
            {stale.map(({ person, last }) => (
              <HStack key={person.id} py={3} borderTop="1px solid" borderColor="line" justify="space-between" align="baseline" flexWrap="wrap" gap={2}>
                <Text fontWeight={600} fontSize="body">
                  {displayNameFor(person) || 'Somebody'}
                </Text>
                <HStack spacing={3}>
                  <Text fontSize="sm" color="inkDim" fontFamily="mono">
                    {last ? `last on ${last}` : 'never'}
                  </Text>
                  <Button as={RouterLink} to="/manager/one-on-one/" size="xs" variant="ghost">
                    Record one
                  </Button>
                </HStack>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      <Box bg="surface" borderRadius="card" p={5} border="1px solid" borderColor="line">
        <HStack justify="space-between" flexDir={{ base: 'column', sm: 'row' }} align={{ base: 'stretch', sm: 'center' }} spacing={4}>
          <HStack spacing={3} align="flex-start">
            <Box pt="2px">
              <Sparkles size={16} aria-hidden="true" />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight={600} mb={0.5}>
                Not sure what to work on today
              </Text>
              <Text fontSize="sm" color="inkMuted" lineHeight={1.6} maxW="55ch">
                Ask the coach. It knows your floor, where everybody is stuck, and how Jazz coaches
                every one of the thirteen steps.
              </Text>
            </Box>
          </HStack>
          <Button as={RouterLink} to="/coach/" size="md" flexShrink={0}>
            Ask the coach
          </Button>
        </HStack>
      </Box>

      <HStack spacing={4} flexWrap="wrap" rowGap={2}>
        <Button as={RouterLink} to="/manager/system/" variant="ghost" size="sm" rightIcon={<ChevronRight size={14} />}>
          Your whole management system
        </Button>
        <Button as={RouterLink} to="/manager/week/" variant="ghost" size="sm" rightIcon={<ChevronRight size={14} />}>
          This week's rhythm
        </Button>
        <Button as={RouterLink} to="/manager/steps/" variant="ghost" size="sm" rightIcon={<ChevronRight size={14} />}>
          Coaching a step
        </Button>
        <Button as={RouterLink} to="/team/" variant="ghost" size="sm" rightIcon={<ChevronRight size={14} />}>
          Your floor
        </Button>
      </HStack>
    </VStack>
  )
}
